import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { fetchAdminBenchmarks, fetchAdminSubmission } from '../api';
import useNoIndex from '../hooks/useNoIndex';

function findOptionLabel(question, value) {
  return question.options.find((o) => o.value === value)?.label ?? '—';
}

function groupQuestionsByDimension(questions) {
  const groups = {};
  for (const q of questions) {
    if (!groups[q.dimensionKey]) groups[q.dimensionKey] = [];
    groups[q.dimensionKey].push(q);
  }
  return groups;
}

export default function AdminSubmission() {
  useNoIndex();
  const { token } = useParams();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState(null);
  const [benchmarks, setBenchmarks] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAdminSubmission(token)
      .then(setSubmission)
      .catch((err) => {
        if (err?.response?.status === 401) setError('Authentication cancelled or invalid.');
        else if (err?.response?.status === 404) setError('Submission not found.');
        else setError(err?.message || 'Could not load submission.');
      });
  }, [token]);

  useEffect(() => {
    if (!submission) return;
    // Benchmarks are non-critical — swallow errors silently.
    fetchAdminBenchmarks(submission.questionnaireId).then(setBenchmarks).catch(() => {});
  }, [submission]);

  if (error) {
    return (
      <div className="page page--wide">
        <div className="error">{error}</div>
        <Link to="/admin">← Back to submissions</Link>
      </div>
    );
  }
  if (!submission) {
    return <div className="page page--wide"><p className="muted">Loading…</p></div>;
  }

  const snapshot = submission.questionnaireSnapshot;
  const dimensionLabel = (key) => snapshot.dimensions.find((d) => d.key === key)?.label || key;
  const groups = groupQuestionsByDimension(snapshot.questions);
  const benchmarksUnlocked =
    benchmarks && benchmarks.sampleSize >= benchmarks.minSampleSize;

  return (
    <div className="page page--wide">
      <div className="admin-toolbar">
        <button type="button" className="secondary" onClick={() => navigate('/admin')}>
          ← All submissions
        </button>
        <span className="admin-toolbar-info muted small">
          {benchmarks ? (
            benchmarksUnlocked
              ? `Benchmarks active · n=${benchmarks.sampleSize}`
              : `Benchmarks unlock at ${benchmarks.minSampleSize} submissions · currently ${benchmarks.sampleSize}`
          ) : null}
        </span>
      </div>

      <h1>Submission detail</h1>

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="admin-meta-grid">
          <div>
            <div className="admin-meta-label">Email</div>
            <div className="admin-meta-value">{submission.email}</div>
          </div>
          <div>
            <div className="admin-meta-label">Questionnaire</div>
            <div className="admin-meta-value">{snapshot.title} <span className="muted small">({submission.questionnaireSlug})</span></div>
          </div>
          <div>
            <div className="admin-meta-label">Submitted</div>
            <div className="admin-meta-value">{new Date(submission.createdAt).toLocaleString()}</div>
          </div>
          <div>
            <div className="admin-meta-label">Score</div>
            <div className="admin-meta-value">
              <strong>{submission.score}</strong> &middot; {submission.tier}
              {benchmarksUnlocked && (
                <span className="muted small" style={{ marginLeft: '0.5rem' }}>
                  (avg {benchmarks.averageScore.toFixed(1)})
                </span>
              )}
            </div>
          </div>
          <div>
            <div className="admin-meta-label">Result link</div>
            <div className="admin-meta-value">
              <Link to={`/results/${submission.token}`}>What the client sees →</Link>
            </div>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h3>Business context</h3>
        <div className="admin-meta-grid">
          <div>
            <div className="admin-meta-label">Industry</div>
            <div className="admin-meta-value">{submission.context?.industry || '—'}</div>
          </div>
          <div>
            <div className="admin-meta-label">Employees</div>
            <div className="admin-meta-value">{submission.context?.employees || '—'}</div>
          </div>
          <div>
            <div className="admin-meta-label">Annual revenue</div>
            <div className="admin-meta-value">{submission.context?.revenue || '—'}</div>
          </div>
          <div>
            <div className="admin-meta-label">Top 3 software tools</div>
            <div className="admin-meta-value">{submission.context?.topTools || '—'}</div>
          </div>
        </div>
        {submission.context?.mainConcern && (
          <div style={{ marginTop: '1rem' }}>
            <div className="admin-meta-label">Main thing they&apos;re trying to figure out</div>
            <div className="admin-meta-value" style={{ fontStyle: 'italic' }}>
              &ldquo;{submission.context.mainConcern}&rdquo;
            </div>
          </div>
        )}
      </div>

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h3>Dimension scores</h3>
        <table>
          <thead>
            <tr>
              <th>Dimension</th>
              <th>Score</th>
              <th>%</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(submission.dimensions).map(([key, d]) => (
              <tr key={key}>
                <td>{dimensionLabel(key)}</td>
                <td>{d.score} / {d.max}</td>
                <td>{d.percent}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 style={{ marginTop: '2rem' }}>Their answers</h2>
      {Object.entries(groups).map(([dimKey, questions]) => (
        <div key={dimKey} className="admin-answer-group">
          <h3>{dimensionLabel(dimKey)}</h3>
          <ol className="admin-answer-list">
            {questions.map((q) => {
              const value = submission.answers?.[q.id];
              const label = findOptionLabel(q, value);
              const avg = benchmarks?.perQuestion?.[q.id];
              return (
                <li key={q.id}>
                  <div className="admin-answer-question">{q.text}</div>
                  <div className="admin-answer-chosen">
                    <span className="admin-answer-points">{value} pts</span>
                    <span className="admin-answer-text">{label}</span>
                    {benchmarksUnlocked && typeof avg === 'number' && (
                      <span
                        className="admin-answer-bench"
                        title={`Average across ${benchmarks.sampleSize} submissions`}
                      >
                        avg {avg.toFixed(1)}
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      ))}

      <div style={{ marginTop: '3rem' }}>
        <button type="button" className="secondary" onClick={() => navigate('/admin')}>
          ← All submissions
        </button>
      </div>
    </div>
  );
}
